import { useState } from 'react'
import { useTenantGroups } from '../../hooks/useTenantGroups'
import { useActionRunner } from '../../hooks/useActionRunner'
import { rebuildConfigCache } from '../../services/api/rebuildConfigCache'
import { ActionFeedback } from '../../components/ActionFeedback/ActionFeedback'
import { SearchableSelect } from '../../components/SearchableSelect/SearchableSelect'
import type { ActionError } from '../../services/api/ActionError'
import type { ActionFeedback as ActionFeedbackType } from '../../types'
import '../../components/CreateEntityForm/CreateEntityForm.css'

export function RebuildConfigCachePage() {
  const { tenantGroups, isLoading: isLoadingGroups } = useTenantGroups()
  const { run, isSubmitting, feedback } = useActionRunner()
  const [tenantGroupCode, setTenantGroupCode] = useState('')
  const tenantGroupOptions = tenantGroups.map((group) => ({
    value: group.code,
    label: group.code,
  }))

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isSubmitting) {
      return
    }
    const code = tenantGroupCode || null
    await run(
      (token) => rebuildConfigCache(token, code),
      (error: ActionError): ActionFeedbackType | null => {
        if (error.status !== 404) {
          return null
        }
        return {
          kind: 'notFound',
          message: `Tenant group '${tenantGroupCode}' was not found.`,
        }
      }
    )
  }

  return (
    <div className="rebuild-config-cache-page">
      <div className="alert alert-info" role="note">
        Select a tenant group to rebuild only its config cache, or leave it as{' '}
        <strong>All tenant groups</strong> to rebuild the config cache for every
        tenant group.
      </div>

      <ActionFeedback feedback={feedback} />

      <form onSubmit={handleSubmit}>
        <div className="create-form-table">
          <div className="create-form-row--last">
            <div className="create-form-label">Tenant Group</div>
            <div className="create-form-input-cell">
              <SearchableSelect
                value={tenantGroupCode}
                options={tenantGroupOptions}
                placeholder="All tenant groups (full rebuild)"
                ariaLabel="Tenant Group"
                isDisabled={isLoadingGroups}
                onChange={setTenantGroupCode}
              />
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
            )}
            Rebuild config cache
          </button>
        </div>
      </form>
    </div>
  )
}
